/**
 * FillerWordSidebar - Review queue for filler words in context.
 * Each item is a card-like row with hover interactions.
 * Uses filteredOccurrences to respect avoidHarshCuts setting.
 * Follows Libretto's calm, editorial design system.
 */
import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, IconButton } from '@mui/material';
import {
    Close as CloseIcon,
    PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { FillerWordStore } from './FillerWordStore';
import { FillerWordOccurrence, formatTimestamp } from './fillerWord';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';

interface FillerWordSidebarProps {
    onClose: () => void;
}

const FillerWordSidebar: React.FC<FillerWordSidebarProps> = observer(({ onClose }) => {
    // Use filteredOccurrences to respect avoidHarshCuts toggle
    const occurrences = FillerWordStore.filteredOccurrences;
    const currentIndex = FillerWordStore.currentIndex;
    
    const handleItemClick = (index: number) => {
        FillerWordStore.goToIndex(index);
        const occurrence = FillerWordStore.filteredOccurrences[index];
        if (occurrence) {
            // Map source time through segments to get correct composition time after cuts
            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(occurrence.startSec);
            if (compTime !== null) {
                CompositionService.seek(compTime);
            } else {
                // Fallback if mapping fails
                CompositionService.seek(occurrence.startSec);
            }
        }
    };
    
    const handlePlayContext = async (occurrence: FillerWordOccurrence) => {
        // Get context words with timestamps to determine playback range
        const context = FillerWordStore.getContextWordsWithTimestamps(occurrence);
        
        let playStartSec: number;
        if (context && context.wordsBefore.length > 0) {
            // Start from the first context word before the filler
            playStartSec = context.wordsBefore[0].startSec;
        } else {
            // Just start from the filler word itself with a small buffer
            playStartSec = Math.max(0, occurrence.startSec - 0.5);
        }
        
        // Map source time to composition time
        const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(playStartSec);
        const seekTime = compTime !== null ? compTime : playStartSec;
        
        // Seek and play
        await CompositionService.seek(seekTime);
        await CompositionService.play();
    };
    
    // Show info about filtered vs total when smooth cuts is on
    const totalCount = FillerWordStore.totalCount;
    const filteredCount = occurrences.length;
    const isFiltered = FillerWordStore.avoidHarshCuts && totalCount !== filteredCount;
    
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#FAFAFA',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 20px',
                    backgroundColor: '#fff',
                    borderBottom: '1px solid #E5E7EB',
                }}
            >
                <Typography sx={{ 
                    fontSize: 14, 
                    fontWeight: 600, 
                    color: '#111827', 
                    flex: 1,
                    letterSpacing: '-0.01em',
                }}>
                    Filler words
                </Typography>
                <Typography sx={{ 
                    fontSize: 12, 
                    color: '#9CA3AF',
                    mr: 2,
                }}>
                    {isFiltered ? (
                        <>{filteredCount} of {totalCount}</>
                    ) : (
                        <>{filteredCount} found</>
                    )}
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: '#9CA3AF',
                        '&:hover': { backgroundColor: '#F3F4F6', color: '#374151' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            {/* Results list */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px',
                }}
            >
                {occurrences.length === 0 ? (
                    <Box sx={{ 
                        padding: '40px 20px', 
                        textAlign: 'center',
                    }}>
                        <Typography sx={{ fontSize: 13, color: '#9CA3AF' }}>
                            {isFiltered 
                                ? 'All filler words would cause harsh cuts'
                                : 'No filler words found'
                            }
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {occurrences.map((occurrence, index) => (
                            <FillerWordResultItem
                                key={occurrence.id}
                                occurrence={occurrence}
                                isSelected={index === currentIndex}
                                onClick={() => handleItemClick(index)}
                                onPlay={() => handlePlayContext(occurrence)}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
});

interface FillerWordResultItemProps {
    occurrence: FillerWordOccurrence;
    isSelected: boolean;
    onClick: () => void;
    onPlay: () => void;
}

const FillerWordResultItem: React.FC<FillerWordResultItemProps> = ({
    occurrence,
    isSelected,
    onClick,
    onPlay,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    
    // Build context string with filler word highlighted
    const contextBefore = occurrence.contextBefore.join(' ');
    const contextAfter = occurrence.contextAfter.join(' ');
    const displayStartSec = ScriptLayerStore.mapSourceTimeToCompositionTime(occurrence.startSec) ?? occurrence.startSec;
    
    const handlePlayClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Don't trigger the item click
        onPlay();
    };
    
    return (
        <Box
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '12px 14px',
                cursor: 'pointer',
                borderRadius: '10px',
                backgroundColor: isSelected ? '#fff' : 'transparent',
                border: isSelected ? '1px solid #E5E7EB' : '1px solid transparent',
                boxShadow: isSelected ? '0px 1px 3px rgba(0, 0, 0, 0.04)' : 'none',
                position: 'relative',
                transition: 'all 0.1s ease',
                '&:hover': {
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                },
            }}
        >
            {/* Left accent bar for selected item */}
            {isSelected && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: 24,
                        backgroundColor: '#2B6BFD',
                        borderRadius: '0 2px 2px 0',
                    }}
                />
            )}
            
            {/* Timestamp - left aligned for scannability */}
            <Typography
                sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#6B7280',
                    flexShrink: 0,
                    width: 48,
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {formatTimestamp(displayStartSec)}
            </Typography>
            
            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0, ml: 1.5 }}>
                <Typography
                    sx={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: '#374151',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {contextBefore && (
                        <span style={{ color: '#9CA3AF' }}>...{contextBefore} </span>
                    )}
                    <span style={{ 
                        fontWeight: 600, 
                        color: '#111827',
                        backgroundColor: '#FEF3C7',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        marginLeft: 2,
                        marginRight: 2,
                    }}>
                        {occurrence.word}
                    </span>
                    {occurrence.isRepeatedWord && (
                        <span style={{ 
                            fontSize: 10, 
                            color: '#9CA3AF', 
                            marginLeft: 2,
                            fontStyle: 'italic',
                        }}>
                            (repeated)
                        </span>
                    )}
                    {contextAfter && (
                        <span style={{ color: '#9CA3AF' }}> {contextAfter}...</span>
                    )}
                </Typography>
            </Box>
            
            {/* Play button - only on hover */}
            <Box
                sx={{
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.1s ease',
                    ml: 1,
                    flexShrink: 0,
                }}
            >
                <IconButton
                    size="small"
                    onClick={handlePlayClick}
                    sx={{
                        padding: '4px',
                        color: '#6B7280',
                        '&:hover': {
                            color: '#2B6BFD',
                            backgroundColor: 'rgba(43, 107, 253, 0.08)',
                        },
                    }}
                >
                    <PlayIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default FillerWordSidebar;
